import CheckboxInput from "components/inputs/checkboxInput";
import NumberInput from "components/inputs/numberInput";
import TextInput from "components/inputs/textInput";
import TextAreaInput from "components/inputs/textareaInput";
import MultiSelectInput from "components/inputs/multiSelectInput";
import RadioButtonInput from "components/inputs/radioButtonInput";
import Section from "components/layout/section";
import { useEffect, useState } from "react";
import { Category, CustomInput } from "types";
import { logger } from "../../../../utils/secureLogger";
import InputGroup from "@/components/inputs/InputGroup";

interface CustomInputsProps {
    storageId: string
    categoryIds: string[]
    customInputs: CustomInput
    setCustomInputs: (customInputs: CustomInput) => void
    categories: Category[]
}

const CustomInputs: React.FC<CustomInputsProps> = ({storageId, categoryIds, customInputs, setCustomInputs, categories}) => {

    const [initilized, setInitialized] = useState(false)

    // no-op: categories are loaded via hook

    // Initilize component.
    useEffect(() => {
        if (initilized) return
        setInitialized(true)
    }, [initilized])

    useEffect(() => {
        // For each category, check if the custom input is not filled in.
        // If so set the default value.
        for (const categoryId of categoryIds) {
            const category = categories.find(c => c.id === categoryId)
            if (
                !category ||
                !category.customInputs?.customInputGroupOrder ||
                !category.customInputs?.customInputGroups
            ) continue
            for (const customInputGroupId of category.customInputs.customInputGroupOrder) {
                const customInputGroup = category.customInputs.customInputGroups ? category.customInputs.customInputGroups[customInputGroupId] : undefined
                if (!customInputGroup) continue
                for (const customInputId of customInputGroup.customInputOrder) {
                    const customInput = customInputGroup.customInputs[customInputId]
                    if (!customInput) continue
                    if (customInput.defaultValue !== undefined && (!customInputs[categoryId] || customInputs[categoryId][customInputId] === undefined)) {
                        setCustomInputs({
                            ...customInputs,
                            [categoryId]: {
                                ...customInputs[categoryId],
                                [customInputId]: customInput.defaultValue
                            }
                        })
                    }
                }
            }
        }
    }, [categories, categoryIds, customInputs, setCustomInputs])


    // If no categories are selected, return null.
    if (categoryIds.length === 0) return null

    // Else return the custom inputs.
    return (
        <>
            {categoryIds.map((categoryId) => {
                const category = categories.find(c => c.id === categoryId)
                if (
                    !category ||
                    !category.customInputs?.customInputGroupOrder ||
                    !category.customInputs?.customInputGroups
                ) return null
                return category.customInputs.customInputGroupOrder.map((customInputGroupId: string) => {
                    const customInputGroup = category.customInputs?.customInputGroups ? category.customInputs.customInputGroups[customInputGroupId] : undefined
                    if (!customInputGroup || !customInputGroup.customInputOrder.length) return null
                    return (
                        <Section key={`${categoryId}-${customInputGroupId}`} title={customInputGroupId}>
                            {(() => {
                                const renderControl = (customInputId: string) => {
                                    const customInput = customInputGroup.customInputs[customInputId]

                                    const setCustomInput = (v: string|number|boolean) => {
                                        logger.debug('Custom input value updated', { 
                                            categoryId, 
                                            customInputId, 
                                            value: v 
                                        })
                                        const newCustomInputs = {
                                            ...customInputs,
                                            [categoryId]: {
                                                ...customInputs[categoryId],
                                                [customInputId]: v
                                            }
                                        }
                                        setCustomInputs(newCustomInputs)
                                    }

                                    if (!customInput) return null

                                    const value = customInputs[categoryId] ? customInputs[categoryId][customInputId] !== undefined ? customInputs[categoryId][customInputId] as string : customInput.defaultValue : customInput.defaultValue

                                    if (customInput.type === 'text') {
                                        return (
                                            <TextInput
                                                required={customInput.required}
                                                label={customInputId}
                                                value={value as string}
                                                onChange={(e) => {setCustomInput(e.target.value)}}
                                                maxLength={200}
                                                showCharacterCount={true}
                                                key={customInputId} />
                                        )
                                    } else if (customInput.type === 'number') {
                                        return (
                                            <NumberInput
                                                required={customInput.required}
                                                label={customInputId}
                                                value={value as string | number}
                                                onChange={(e) => {setCustomInput(e.target.value)}}
                                                key={customInputId} />
                                        )
                                    } else if (customInput.type === 'checkbox') {
                                        return (
                                            <CheckboxInput
                                                required={customInput.required}
                                                label={customInputId}
                                                checked={value as boolean | string}
                                                onChange={(e) => {setCustomInput(e.target.checked)}}
                                                key={customInputId} />
                                        )
                                    } else if (customInput.type === 'textarea') {
                                        return (
                                            <TextAreaInput
                                                required={customInput.required}
                                                label={customInputId}
                                                value={value as string}
                                                onChange={(e) => {setCustomInput(e.target.value)}}
                                                maxLength={2000}
                                                showCharacterCount={true}
                                                key={customInputId} />
                                        )
                                    } else if (customInput.type === 'select') {
                                        return (
                                            <MultiSelectInput
                                                required={customInput.required}
                                                label={customInputId}
                                                value={value as string}
                                                options={customInput.options || []}
                                                onChange={(e) => {setCustomInput(e.target.value)}}
                                                key={customInputId} />
                                        )
                                    } else if (customInput.type === 'radio') {
                                        return (
                                            <RadioButtonInput
                                                required={customInput.required}
                                                label={customInputId}
                                                value={value as string}
                                                options={customInput.options || []}
                                                name={`${categoryId}_${customInputId}`}
                                                onChange={(e) => {setCustomInput(e.target.value)}}
                                                key={customInputId} />
                                        )
                                    } else {
                                        return null
                                    }
                                }

                                const rows: React.ReactNode[] = []
                                let batch: string[] = []
                                const flush = (suffix: string) => {
                                    if (batch.length === 0) return
                                    const ids = batch
                                    batch = []
                                    rows.push(
                                        <InputGroup key={`${customInputGroupId}-${suffix}`}>
                                            {ids.map((id) => renderControl(id))}
                                        </InputGroup>
                                    )
                                }

                                customInputGroup.customInputOrder.forEach((customInputId, index) => {
                                    const customInput = customInputGroup.customInputs[customInputId]
                                    if (customInput?.type === 'textarea' || customInput?.type === 'checkbox') {
                                        flush(`before-${index}`)
                                        rows.push(renderControl(customInputId))
                                    } else {
                                        batch.push(customInputId)
                                    }
                                })
                                flush('end')

                                return rows
                            })()}
                        </Section>
                    )
                })
            })}
        </>
    )
}

export default CustomInputs

